import { useUpsertDefaultRecordValueConfiguration } from "#src/components/hooks/adapters/useDefaultRecordValueConfig";
import { useMeasurementTypes } from "#src/contexts/MeasurementTypeContext";
import type { FilterBuilderFilterType } from "@validereinc/common-components";
import {
  Button,
  Card,
  CheckboxInput,
  Dialog,
  DropdownInput,
  FilterBuilder,
  Form,
  TextInput,
  useFieldArray,
  useForm,
  useToast,
} from "@validereinc/common-components";
import type { DefaultRecordValueConfigurationType } from "@validereinc/domain";
import {
  DefaultRecordValueConfigurationTypeNames as DRVCTypeNames,
  DefaultRecordValueConfigurationSchema,
} from "@validereinc/domain";
import classNames from "classnames/bind";
import React, { useEffect, useMemo, useState } from "react";
import { TimezoneDropdownInput } from "../../Dropdowns/TimezoneDropdownInput";
import {
  AGGREGATE_FUNCTION_OPTIONS,
  FILTER_FIELDS_BY_CONFIGURATION_TYPE,
  FilterEntryType,
  SORT_OPTIONS_BY_CONFIGURATION_TYPE,
} from "./EditDefaultRecordValueConfigurationDialog.helpers";
import styles from "./EditDefaultRecordValueConfigurationDialog.module.scss";

const cx = classNames.bind(styles);

export type EditDefaultRecordValueConfigurationDialogProps = {
  /** The item to edit, or undefined if dialog should be closed */
  itemToEdit: DefaultRecordValueConfigurationType | undefined;
  /** Callback when the dialog should close */
  onClose: () => void;
};

/**
 * Dialog for editing a default record value configuration (DRVC).
 * Uses the upsert endpoint to update the configuration.
 */
export const EditDefaultRecordValueConfigurationDialog = ({
  itemToEdit,
  onClose,
}: EditDefaultRecordValueConfigurationDialogProps) => {
  const editForm = useForm({});
  const { toast } = useToast();
  const upsertDefaultRecordValueConfiguration =
    useUpsertDefaultRecordValueConfiguration({
      successMessage: "Default Record Value Configuration updated successfully",
    });

  const { measurementTypes, getUnitsByQuantity } = useMeasurementTypes();

  const watchedMeasurementTypeId = editForm.watch("measurement_type");
  const watchedMeasurementUnit = editForm.watch("measurement_unit");
  const selectedMeasurementType = useMemo(
    () => measurementTypes.find(({ id }) => id === watchedMeasurementTypeId),
    [measurementTypes, watchedMeasurementTypeId]
  );
  const availableUnits = useMemo(() => {
    return getUnitsByQuantity(selectedMeasurementType?.quantity);
  }, [getUnitsByQuantity, selectedMeasurementType?.quantity]);

  const [additionalFilter, setAdditionalFilter] =
    useState<FilterBuilderFilterType>({});

  const {
    fields: weightedFilterFields,
    append: appendWeightedFilter,
    remove: removeWeightedFilter,
  } = useFieldArray({
    control: editForm.control,
    name: "measurement_weighted_values_filter_entries",
  });

  useEffect(() => {
    if (itemToEdit) {
      const windowValue = itemToEdit.configuration?.window;
      const windowMatch = windowValue?.match(/^(\d+)(month|year)$/);

      const filterObject = (
        itemToEdit.configuration as { filter?: Record<string, unknown> }
      )?.filter;
      setAdditionalFilter(filterObject ?? {});

      const weightedFilterObject = (
        itemToEdit.configuration as {
          measurement_weighted_values_filter?: Record<string, unknown>;
        }
      )?.measurement_weighted_values_filter;
      const weightedFilterEntries = weightedFilterObject
        ? Object.entries(weightedFilterObject).map(([key, value]) => ({
            key,
            value:
              typeof value === "object" && value !== null
                ? JSON.stringify(value)
                : String(value),
          }))
        : [
            { key: "measurement.measurement_type", value: "" },
            { key: "measurement.measurement_unit", value: "" },
          ];

      editForm.reset({
        measurement_type: itemToEdit.measurement_type,
        measurement_unit: itemToEdit.measurement_unit,
        default_value: itemToEdit.default_value ?? "",
        description: itemToEdit.description ?? "",
        advanced_configuration: !!itemToEdit.configuration,
        configuration_type: itemToEdit.configuration?.configuration_type ?? "",
        aggregate_function:
          itemToEdit.configuration?.aggregate_function ?? "top",
        window_enabled: !!windowMatch,
        window_value: windowMatch ? windowMatch[1] : "",
        window_unit: windowMatch ? windowMatch[2] : "month",
        sort: (itemToEdit.configuration as { sort?: string })?.sort ?? "",
        sort_direction:
          (itemToEdit.configuration as { sort_direction?: string })
            ?.sort_direction ?? "desc",
        timezone:
          (itemToEdit.configuration as { timezone?: string })?.timezone ?? "",
        // Form measurement specific fields
        second_sort:
          (itemToEdit.configuration as { second_sort?: string })?.second_sort ??
          "",
        time_weighted_interpolation_method:
          (
            itemToEdit.configuration as {
              time_weighted_interpolation_method?: string;
            }
          )?.time_weighted_interpolation_method ?? "",
        time_weighted_interpolation_interval:
          (
            itemToEdit.configuration as {
              time_weighted_interpolation_interval?: string;
            }
          )?.time_weighted_interpolation_interval ?? "",
        use_measurement_weighted_values:
          (
            itemToEdit.configuration as {
              use_measurement_weighted_values?: boolean;
            }
          )?.use_measurement_weighted_values ?? false,
        measurement_weighted_values_filter_enabled: !!weightedFilterObject,
        measurement_weighted_values_filter_entries: weightedFilterEntries,
      });
    }
  }, [itemToEdit]);

  const isAdvancedConfigurationEnabled = editForm.watch(
    "advanced_configuration"
  );
  const isWindowEnabled = editForm.watch("window_enabled");
  const configurationTypeValue = editForm.watch("configuration_type");
  const isMeasurementSeries = configurationTypeValue === "measurement_series";
  const isFormMeasurement = configurationTypeValue === "form_measurement";
  const isWeightedFilterEnabled = editForm.watch(
    "measurement_weighted_values_filter_enabled"
  );

  const sortOptions = useMemo(
    () =>
      SORT_OPTIONS_BY_CONFIGURATION_TYPE[configurationTypeValue ?? ""] ?? [],
    [configurationTypeValue]
  );

  const filterBuilderFields = useMemo(
    () =>
      FILTER_FIELDS_BY_CONFIGURATION_TYPE[configurationTypeValue ?? ""] ?? {},
    [configurationTypeValue]
  );

  useEffect(() => {
    if (
      selectedMeasurementType &&
      !availableUnits.find((unit) => unit.id === watchedMeasurementUnit)
    ) {
      editForm.setValue(
        "measurement_unit",
        selectedMeasurementType?.default_unit
      );
    }
  }, [selectedMeasurementType, watchedMeasurementUnit, availableUnits]);

  const handleEditSubmit = editForm.handleSubmit((values) => {
    if (!itemToEdit) return;

    const isRecordType = values.configuration_type === "record";
    if (
      values.advanced_configuration &&
      isRecordType &&
      !additionalFilter.measurement_type
    ) {
      toast.push({
        intent: "error",
        description:
          'Measurement type is required in filters for "Record" record automations.',
      });
      return;
    }

    const windowString =
      values.window_enabled && values.window_value && values.window_unit
        ? `${values.window_value}${values.window_unit}`
        : undefined;

    const hasFilterEntries =
      additionalFilter && Object.keys(additionalFilter).length > 0;

    const isFormMeasurementType =
      values.configuration_type === "form_measurement";

    const weightedFilterObject =
      isFormMeasurementType && values.measurement_weighted_values_filter_enabled
        ? values.measurement_weighted_values_filter_entries?.reduce(
            (
              acc: Record<string, unknown>,
              entry: { key: string; value: string }
            ) => {
              if (entry.key && entry.value) {
                try {
                  acc[entry.key] = JSON.parse(entry.value);
                } catch {
                  acc[entry.key] = entry.value;
                }
              }
              return acc;
            },
            {}
          )
        : undefined;

    const configuration = values.advanced_configuration
      ? ({
          configuration_type: values.configuration_type,
          aggregate_function: values.aggregate_function,
          window: windowString,
          sort: values.sort || undefined,
          sort_direction: values.sort_direction || undefined,
          timezone: values.timezone || undefined,
          filter: hasFilterEntries ? additionalFilter : undefined,
          // Form measurement specific fields
          second_sort: isFormMeasurementType
            ? values.second_sort || undefined
            : undefined,
          time_weighted_interpolation_method: isFormMeasurementType
            ? values.time_weighted_interpolation_method || undefined
            : undefined,
          time_weighted_interpolation_interval: isFormMeasurementType
            ? values.time_weighted_interpolation_interval || undefined
            : undefined,
          use_measurement_weighted_values: isFormMeasurementType
            ? (values.use_measurement_weighted_values ?? undefined)
            : undefined,
          measurement_weighted_values_filter: weightedFilterObject,
        } as typeof itemToEdit.configuration)
      : undefined;

    upsertDefaultRecordValueConfiguration.mutate(
      {
        data: {
          asset_id: itemToEdit.asset_id,
          reporting_group_id: itemToEdit.reporting_group_id,
          measurement_type: values.measurement_type,
          measurement_unit: values.measurement_unit,
          default_value: values.default_value
            ? Number(values.default_value)
            : null,
          description: values.description ?? "",
          configuration,
        },
      },
      {
        onSuccess: () => {
          onClose();
          editForm.reset();
          setAdditionalFilter({});
        },
      }
    );
  });

  return (
    <Dialog
      isOpen={!!itemToEdit}
      onClose={onClose}
      title="Edit Record Automation"
      actionRow={[
        <Button
          key="save-record-automation"
          variant="primary"
          onClick={handleEditSubmit}
          isLoading={upsertDefaultRecordValueConfiguration.isLoading}
        >
          Save
        </Button>,
      ]}
    >
      <Form {...editForm}>
        <DropdownInput
          label="Record Value Type"
          name={
            DefaultRecordValueConfigurationSchema.keyof().Enum.measurement_type
          }
          options={measurementTypes ?? []}
          valueKey="id"
          labelKey="name"
          isRequired
          isClearable={false}
        />

        <DropdownInput
          label="Unit"
          name={
            DefaultRecordValueConfigurationSchema.keyof().Enum.measurement_unit
          }
          options={availableUnits}
          valueKey="id"
          labelKey="name.symbol"
          isRequired
          isClearable={false}
        />

        <TextInput
          name="default_value"
          label="Default Value"
          type="number"
        />

        <TextInput
          name="description"
          label="Description"
          type="text"
          isRequired={false}
        />

        <CheckboxInput
          name="advanced_configuration"
          label="Advanced Configuration"
          isLabelShown={false}
        />

        {isAdvancedConfigurationEnabled && (
          <>
            <DropdownInput
              label="Configuration Type"
              name="configuration_type"
              options={Object.entries(DRVCTypeNames).map(([value, label]) => ({
                value,
                label,
              }))}
              valueKey="value"
              labelKey="label"
              isRequired
              isClearable={false}
              onChange={() => {
                editForm.setValue("sort", "");
                setAdditionalFilter({});
              }}
            />

            <DropdownInput
              label="Aggregate Function"
              name="aggregate_function"
              options={AGGREGATE_FUNCTION_OPTIONS}
              valueKey="value"
              labelKey="label"
              isRequired
              isClearable={false}
            />

            <CheckboxInput
              name="window_enabled"
              label="Enable Window"
              isLabelShown={false}
            />

            {isWindowEnabled && (
              <div className={cx("fieldRow")}>
                <TextInput
                  name="window_value"
                  label="Window"
                  type="number"
                  isRequired
                  className={cx("fieldRowItem")}
                />

                <DropdownInput
                  name="window_unit"
                  label="Window Unit"
                  isRequired
                  options={[
                    { label: "Month", value: "month" },
                    { label: "Year", value: "year" },
                  ]}
                  valueKey="value"
                  labelKey="label"
                  className={cx("fieldRowItem")}
                  isClearable={false}
                />
              </div>
            )}

            {!isMeasurementSeries && (
              <div className={cx("fieldRow")}>
                <DropdownInput
                  name="sort"
                  label="Sort"
                  options={sortOptions}
                  valueKey="value"
                  labelKey="label"
                  isRequired
                  isClearable={false}
                  className={cx("fieldRowItem")}
                />

                <DropdownInput
                  name="sort_direction"
                  label="Sort Direction"
                  isRequired
                  isClearable={false}
                  options={[
                    { label: "Ascending", value: "asc" },
                    { label: "Descending", value: "desc" },
                  ]}
                  valueKey="value"
                  labelKey="label"
                  className={cx("fieldRowItem")}
                />
              </div>
            )}

            <TimezoneDropdownInput
              name="timezone"
              isRequired={false}
            />

            <FilterBuilder
              title="Additional Filters"
              fields={filterBuilderFields}
              filter={additionalFilter}
              setFilter={setAdditionalFilter}
              showInputModeToggle={false}
              variant="vertical"
              maxDepth={2}
            />

            {isFormMeasurement && (
              <Card className={cx("advancedOptionsCard")}>
                <div className={cx("advancedOptionsHeader")}>
                  <strong>Form Measurement Advanced Options</strong>
                </div>

                <DropdownInput
                  name="second_sort"
                  label="Second Sort"
                  options={sortOptions}
                  valueKey="value"
                  labelKey="label"
                  isClearable
                />

                <DropdownInput
                  label="Time Weighted Interpolation Method"
                  name="time_weighted_interpolation_method"
                  options={[{ label: "Forward Fill", value: "forward_fill" }]}
                  valueKey="value"
                  labelKey="label"
                  isClearable
                />

                <DropdownInput
                  label="Time Weighted Interpolation Interval"
                  name="time_weighted_interpolation_interval"
                  options={[
                    { label: "Day", value: "day" },
                    { label: "Hour", value: "hour" },
                  ]}
                  valueKey="value"
                  labelKey="label"
                  isClearable
                />

                <CheckboxInput
                  name="use_measurement_weighted_values"
                  label="Use Measurement Weighted Values"
                  isLabelShown={false}
                />

                <CheckboxInput
                  name="measurement_weighted_values_filter_enabled"
                  label="Enable Measurement Weighted Values Filter"
                  isLabelShown={false}
                />

                {isWeightedFilterEnabled && (
                  <Card className={cx("nestedFilterCard")}>
                    <div className={cx("nestedFilterHeader")}>
                      <strong>Measurement Weighted Values Filter</strong>
                      <Button
                        onClick={() =>
                          appendWeightedFilter({ key: "", value: "" })
                        }
                        icon="plus"
                        variant="outline"
                      >
                        Add Entry
                      </Button>
                    </div>

                    {(weightedFilterFields as FilterEntryType[]).map(
                      (field, index) => (
                        <div
                          key={field.id}
                          className={cx("filterRow")}
                        >
                          <div className={cx("fieldRowItem")}>
                            <TextInput
                              name={`measurement_weighted_values_filter_entries.${index}.key`}
                              label="Key"
                              type="text"
                              isRequired
                              isFluid
                              readOnly={
                                field.key === "measurement.measurement_type" ||
                                field.key === "measurement.measurement_unit"
                              }
                            />
                          </div>
                          <div className={cx("fieldRowItem")}>
                            <TextInput
                              name={`measurement_weighted_values_filter_entries.${index}.value`}
                              label="Value"
                              type="text"
                              isRequired
                              isFluid
                            />
                          </div>
                          <Button
                            onClick={() => removeWeightedFilter(index)}
                            icon="trash"
                            variant="error-outline"
                            className={cx("filterRowDeleteButton")}
                            disabled={
                              field.key === "measurement.measurement_type" ||
                              field.key === "measurement.measurement_unit"
                            }
                          />
                        </div>
                      )
                    )}

                    {weightedFilterFields.length === 0 && (
                      <div className={cx("emptyState")}>
                        No filter entries configured
                      </div>
                    )}
                  </Card>
                )}
              </Card>
            )}
          </>
        )}
      </Form>
    </Dialog>
  );
};
